import { useEffect, useRef, useState } from 'react';
import { router } from '@inertiajs/react';
import { DanceLoader } from './DanceLoader';
import { getCurrentPageStyles } from './DanceStyleLoaderContext';
import { MIN_LOADER_MS, type DanceStyleLike } from '../../../src/constants/danceLoaders';
import './PageChangeLoader.css';

const DIM_CLASS = 'wcwd-page-changing';

// A page change is a GET to a different path. Same-page reloads (filters,
// pagination, `only:` partials) have their own section loaders.
function isPageChange(visit: { url: URL; method: string; only: string[] }): boolean {
  return visit.method === 'get' && visit.only.length === 0 && visit.url.pathname !== window.location.pathname;
}

// Replaces Inertia's top progress bar (#1885): a dance-count pill appears in
// the center of the screen and the current page dims until the next one is ready.
// Held at least MIN_LOADER_MS so the count always lands.
interface PageChangeLoaderProps {
  minMs?: number;
}

export function PageChangeLoader({ minMs = MIN_LOADER_MS }: PageChangeLoaderProps) {
  const [visitId, setVisitId] = useState<number | null>(null);
  const [styles, setStyles] = useState<DanceStyleLike[]>([]);
  const startedAt = useRef(0);
  const hideTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    let count = 0;
    let pending = false;

    const hide = () => {
      setVisitId(null);
      document.body.classList.remove(DIM_CLASS);
    };

    const offStart = router.on('start', (event) => {
      if (!isPageChange(event.detail.visit)) return;
      clearTimeout(hideTimer.current);
      if (!pending) {
        startedAt.current = Date.now();
        count += 1;
        setVisitId(count);
        setStyles(getCurrentPageStyles());
        document.body.classList.add(DIM_CLASS);
      }
      pending = true;
    });

    const offFinish = router.on('finish', () => {
      if (!pending) return;
      pending = false;
      const remaining = minMs - (Date.now() - startedAt.current);
      if (remaining <= 0) hide();
      else hideTimer.current = setTimeout(hide, remaining);
    });

    return () => {
      offStart();
      offFinish();
      clearTimeout(hideTimer.current);
      document.body.classList.remove(DIM_CLASS);
    };
  }, [minMs]);

  if (visitId === null) return null;

  return (
    <div className="page-change-loader">
      {/* key: a fresh random pick for every page change */}
      <DanceLoader key={visitId} danceStyles={styles} />
    </div>
  );
}
